/**
 * Token Test Utilities
 * Helper functions for testing token expiration scenarios
 */

import { AuthTokenManager } from '../TokenManager';

/**
 * Create an expired JWT token for testing
 * @param {number} expiredSecondsAgo - How many seconds ago the token should be expired
 * @returns {string} Expired JWT token
 */
export const createExpiredToken = (expiredSecondsAgo = 3600) => {
  const now = Math.floor(Date.now() / 1000);
  const expiredTime = now - expiredSecondsAgo;
  
  const header = {
    alg: 'HS256',
    typ: 'JWT'
  };
  
  const payload = {
    sub: 'test-user',
    iat: expiredTime - 3600, // Issued 1 hour before expiry
    exp: expiredTime, // Expired
    role: 'User',
    email: 'test@example.com'
  };
  
  // Create a simple JWT (this is just for testing, not secure)
  const encodedHeader = btoa(JSON.stringify(header));
  const encodedPayload = btoa(JSON.stringify(payload));
  const signature = 'test-signature'; // Not real signature
  
  return `${encodedHeader}.${encodedPayload}.${signature}`;
};

/**
 * Set an expired token in localStorage for testing
 * @param {number} expiredSecondsAgo - How many seconds ago the token should be expired
 */
export const setExpiredToken = (expiredSecondsAgo = 3600) => {
  const expiredToken = createExpiredToken(expiredSecondsAgo);
  AuthTokenManager.setTokens(expiredToken, 'refresh-token', 1800);
  console.log('🔍 TokenTestUtils: Set expired token for testing');
};

/**
 * Set a token that will expire soon (for testing auto-refresh)
 * @param {number} expiresInSeconds - How many seconds until the token expires
 */
export const setSoonToExpireToken = (expiresInSeconds = 60) => {
  const now = Math.floor(Date.now() / 1000);
  const expTime = now + expiresInSeconds;
  
  const header = {
    alg: 'HS256',
    typ: 'JWT'
  };
  
  const payload = {
    sub: 'test-user',
    iat: now - 1800, // Issued 30 minutes ago
    exp: expTime, // Will expire soon
    role: 'User',
    email: 'test@example.com'
  };
  
  const encodedHeader = btoa(JSON.stringify(header));
  const encodedPayload = btoa(JSON.stringify(payload));
  const signature = 'test-signature';
  
  const token = `${encodedHeader}.${encodedPayload}.${signature}`;
  AuthTokenManager.setTokens(token, 'refresh-token', expiresInSeconds);
  console.log(`🔍 TokenTestUtils: Set token that expires in ${expiresInSeconds} seconds`);
};

/**
 * Clear all tokens
 */
export const clearAllTokens = () => {
  AuthTokenManager.clearTokens();
  console.log('🔍 TokenTestUtils: Cleared all tokens');
};

/**
 * Get current token info for debugging
 */
export const getTokenInfo = () => {
  const token = AuthTokenManager.getAccessToken();
  if (!token) {
    return { exists: false };
  }
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Math.floor(Date.now() / 1000);
    const isExpired = payload.exp < now;
    const timeUntilExpiry = payload.exp - now;
    
    return {
      exists: true,
      isExpired,
      timeUntilExpiry,
      expiresAt: new Date(payload.exp * 1000).toISOString(),
      payload
    };
  } catch (error) {
    return {
      exists: true,
      isExpired: true,
      error: 'Invalid token format'
    };
  }
};

/**
 * Simulate backend response with "Invalid or inactive token" message
 * This simulates the exact backend response for session conflicts
 */
export const simulateBackendSessionConflict = () => {
  console.log('🔍 TokenTestUtils: Simulating backend session conflict response');
  
  // Create a mock error response that matches the exact backend format
  const mockError = {
    response: {
      status: 401,
      data: {
        detail: 'Invalid or inactive token',
        status: 401,
        type: 'about:blank',
        description: 'The provided token is invalid or has been revoked',
        title: 'Unauthorized'
      }
    }
  };
  
  // Dispatch the error to test the error handling
  window.dispatchEvent(new CustomEvent('test:sessionConflict', {
    detail: mockError
  }));
  
  console.log('🔍 TokenTestUtils: Dispatched test session conflict event with exact backend format');
};

/**
 * Test the complete session conflict flow with exact backend response
 * This function simulates the full error handling pipeline
 */
export const testSessionConflictFlow = async () => {
  console.log('🔍 TokenTestUtils: Testing complete session conflict flow');
  
  // First, set a valid token to simulate a logged-in user
  const validToken = createExpiredToken(-3600); // Token that was valid 1 hour ago
  AuthTokenManager.setTokens(validToken, 'refresh-token', 1800);
  
  console.log('🔍 TokenTestUtils: Set valid token, now simulating backend session conflict...');
  
  // Simulate the exact backend response
  const backendResponse = {
    status: 401,
    data: {
      detail: 'Invalid or inactive token',
      status: 401,
      type: 'about:blank',
      description: 'The provided token is invalid or has been revoked',
      title: 'Unauthorized'
    }
  };
  
  // Create a mock axios error
  const mockAxiosError = {
    response: backendResponse,
    config: { url: '/api/test-endpoint', method: 'GET' },
    request: {},
    message: 'Request failed with status code 401'
  };
  
  console.log('🔍 TokenTestUtils: Mock backend response:', backendResponse);
  
  // Simulate what would happen in the response interceptor
  try {
    // This would normally be called by the response interceptor
    console.log('🔍 TokenTestUtils: Simulating ResponseInterceptor.handleUnauthorizedError...');
    
    // Check if this would trigger session conflict detection
    const detail = backendResponse.data.detail;
    const detailLower = detail.toLowerCase();
    const isSessionConflict = detailLower.includes('invalid or inactive token');
    
    console.log('🔍 TokenTestUtils: Session conflict detection result:', {
      detail,
      detailLower,
      isSessionConflict
    });
    
    if (isSessionConflict) {
      console.log('✅ TokenTestUtils: Session conflict would be detected correctly!');
      console.log('🔍 TokenTestUtils: This would trigger:');
      console.log('  - Token cleanup');
      console.log('  - SessionConflictNotification display');
      console.log('  - User redirect to login');
    } else {
      console.log('❌ TokenTestUtils: Session conflict detection failed!');
    }
    
  } catch (error) {
    console.error('🔍 TokenTestUtils: Error in test flow:', error);
  }
};

// Make functions available globally for testing in browser console
if (typeof window !== 'undefined') {
  window.TokenTestUtils = {
    createExpiredToken,
    setExpiredToken,
    setSoonToExpireToken,
    clearAllTokens,
    getTokenInfo,
    simulateBackendSessionConflict,
    testSessionConflictFlow
  };
}
